import { useAnalyticsContext } from '../providers/AnalyticsProvider';
import { AnalyticsEventParams, AnalyticsUser, XegueiEventName } from '../services/analytics/types';

/**
 * Hook ergonômico para disparar eventos analytics.
 *
 * Exemplo:
 *   const { track, trackLead, trackPurchase } = useAnalytics();
 *   track('ViewContent', { items: [{ id, nome, categoria, valor }] });
 */
export function useAnalytics() {
  const { analytics, config, carregarConfig, identificar } = useAnalyticsContext();

  return {
    track: (event: XegueiEventName, params?: AnalyticsEventParams, user?: AnalyticsUser) =>
      analytics.track(event, params || {}, user),

    page: (params?: AnalyticsEventParams) => analytics.page(params),

    trackViewContent: (item: { id?: string; nome?: string; categoria?: string; valor?: number }) =>
      analytics.track('ViewContent', {
        items: [{ id: item.id, nome: item.nome, categoria: item.categoria, valor: item.valor, quantidade: 1 }],
        value: item.valor,
        currency: 'BRL',
      }),

    trackAddToCart: (item: any, valorTotalCarrinho?: number) =>
      analytics.track('AddToCart', {
        items: [
          {
            id: item?.id,
            nome: item?.descricao || item?.nome,
            categoria: item?.categoria,
            valor: parseValor(item?.subtotal || item?.valor),
            quantidade: item?.quantidade || 1,
          },
        ],
        value: valorTotalCarrinho ?? parseValor(item?.subtotal || item?.valor),
        currency: 'BRL',
      }),

    trackRemoveFromCart: (item: any) =>
      analytics.track('RemoveFromCart', {
        items: [
          {
            id: item?.id,
            nome: item?.descricao || item?.nome,
            categoria: item?.categoria,
            valor: parseValor(item?.subtotal || item?.valor),
            quantidade: item?.quantidade || 1,
          },
        ],
        value: parseValor(item?.subtotal || item?.valor),
        currency: 'BRL',
      }),

    trackInitiateCheckout: (items: any[], valorTotal: number) =>
      analytics.track('InitiateCheckout', {
        items: (items || []).map((i) => ({
          id: i.id,
          nome: i.descricao || i.nome,
          categoria: i.categoria,
          valor: parseValor(i.subtotal || i.valor),
          quantidade: i.quantidade || 1,
        })),
        value: valorTotal,
        currency: 'BRL',
      }),

    trackAddPaymentInfo: (paymentType: string, valorTotal: number) =>
      analytics.track('AddPaymentInfo', {
        paymentType,
        value: valorTotal,
        currency: 'BRL',
      }),

    trackPurchase: (data: {
      orderId: string;
      valor: number;
      items: any[];
      paymentType?: string;
      coupon?: string;
      shipping?: number;
      discount?: number;
      extras?: Record<string, any>;
    }) =>
      analytics.track('Purchase', {
        orderId: data.orderId,
        value: data.valor,
        currency: 'BRL',
        items: (data.items || []).map((i) => ({
          id: i.id,
          nome: i.descricao || i.nome,
          categoria: i.categoria,
          valor: parseValor(i.subtotal || i.valor),
          quantidade: i.quantidade || 1,
        })),
        paymentType: data.paymentType,
        coupon: data.coupon,
        shipping: data.shipping,
        discount: data.discount,
        extras: data.extras,
      }),

    trackLead: (canal: 'whatsapp' | string, extras?: Record<string, any>) =>
      analytics.track('Lead', { extras: { canal, ...(extras || {}) } }),

    trackSearch: (termo: string) => analytics.track('Search', { searchTerm: termo }),

    trackAbandonCheckout: (items: any[], valorTotal: number) =>
      analytics.track('AbandonCheckout', {
        items: (items || []).map((i) => ({
          id: i.id,
          nome: i.descricao || i.nome,
          categoria: i.categoria,
          valor: parseValor(i.subtotal || i.valor),
          quantidade: i.quantidade || 1,
        })),
        value: valorTotal,
        currency: 'BRL',
      }),

    carregarConfig,
    identificar,
    config,
  };
}

/** Converte "12,50" / "1.234,56" / 12.5 em number. */
function parseValor(v: any): number {
  if (typeof v === 'number') return v;
  if (!v) return 0;
  const s = String(v).replace(/\./g, '').replace(/,/g, '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

export default useAnalytics;
